import { Link } from "react-router-dom";
import { SEO } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import { FiArrowRight, FiHome, FiRefreshCw, FiUsers, FiFileText } from "@/lib/icons";
import { broker } from "@/config/broker.config";

interface GuideCard {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  readTime: string;
}

const guides: GuideCard[] = [
  {
    title: "The Loan Process",
    description:
      "A step-by-step guide to understanding how home loans work, from pre-approval to settlement day.",
    href: "/guides/loan-process",
    icon: <FiHome className="size-6" />,
    readTime: "8 min read",
  },
  {
    title: "First Home Buyer Guide",
    description:
      "Everything you need to know about buying your first home, including grants, schemes, and tips.",
    href: "/guides/first-home-buyer-guide",
    icon: <FiHome className="size-6" />,
    readTime: "10 min read",
  },
  {
    title: "Refinancing Guide",
    description:
      "Learn when and how to refinance your home loan to save money or access equity.",
    href: "/guides/refinancing-guide",
    icon: <FiRefreshCw className="size-6" />,
    readTime: "6 min read",
  },
  {
    title: "Why Use a Broker",
    description:
      "Discover the benefits of using a mortgage broker and how we can help you find the right loan.",
    href: "/guides/why-use-a-broker",
    icon: <FiUsers className="size-6" />,
    readTime: "5 min read",
  },
  {
    title: "Document Checklist",
    description:
      "A comprehensive list of documents you'll need for your home loan application. Download and print.",
    href: "/guides/document-checklist",
    icon: <FiFileText className="size-6" />,
    readTime: "Download",
  },
];

export function GuidesPage() {
  return (
    <>
      <SEO
        title="Guides"
        description="Expert guides to help you understand home loans, the buying process, refinancing, and more. Free resources from your trusted mortgage broker."
        url="/guides"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <p className="section-label mb-4">Resources</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              Guides & Resources
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Got questions about the loan process? We've put together these
              guides to help you understand home loans, how it all works, and
              how to choose the right one.
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Understanding your needs
              </h2>
              <p className="text-muted-foreground mb-4">
                When you sit down with us, it's your opportunity to share your
                needs and financial goals. Ask questions about loans,
                applications, approvals, and what follows.
              </p>
              <p className="text-muted-foreground mb-6">
                We'll meet you at your preferred time and location – home,
                office, or café, during the day, night, or weekend.
              </p>
              <p className="text-muted-foreground">
                Sometimes the best way to get an understanding of home loans is
                to speak to a broker in person. But these guides are a great
                starting point.
              </p>
            </div>
            <div className="bg-primary/5 rounded-3xl p-8 lg:p-10">
              <p className="section-label mb-4">Free Consultation</p>
              <p className="font-serif text-2xl mb-4">
                Prefer to speak with someone?
              </p>
              <p className="text-muted-foreground mb-6">
                Book a free consultation and we'll walk you through everything
                in person.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center font-medium text-primary hover:underline underline-offset-4"
              >
                Book a consultation
                <FiArrowRight className="ml-2 size-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Browse Guides</p>
            <h2 className="font-serif text-3xl md:text-4xl">
              How does the process work?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {guides.map((guide) => (
              <Link
                key={guide.href}
                to={guide.href}
                className="group bg-white rounded-2xl p-6 md:p-8 border border-border/50 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/5 rounded-xl flex items-center justify-center text-primary group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                    {guide.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg group-hover:text-primary transition-colors">
                        {guide.title}
                      </h3>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                        {guide.readTime}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {guide.description}
                    </p>
                    <span className="inline-flex items-center mt-4 text-sm font-medium text-primary group-hover:underline underline-offset-4">
                      Read guide
                      <FiArrowRight className="ml-1 size-3 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Need a home loan?"
        title="Whatever your circumstances, we will find the deal that's right for you"
        description="Send through a quick enquiry and we will be in touch."
        primaryAction={{ label: "Get in Touch", href: "/contact" }}
        secondaryAction={{ label: "Call Us", href: `tel:${broker.phone.replace(/\s/g, "")}` }}
      />
    </>
  );
}
